// @ts-nocheck
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import type {
  AccountDeletionResponse,
  AccountPasswordResetConfirmCreate400,
  AccountPasswordResetCreate400,
  AccountRegisterCreate201,
  AccountRegisterCreate400,
  AuthToken,
  EmailVerificationError,
  EmailVerificationResponse,
  LoginRequest,
  MagicLinkErrorResponse,
  MagicLinkLoginResponse,
  MagicLinkRequestResponse,
  PasswordResetConfirm,
  PasswordResetRequest,
  PasswordResetResponse,
  Registration,
  RequestMagicLink,
  TokenRefreshRequest,
  TokenRefreshResponse,
} from "../../models";
import { customInstance } from "../../custom-instance";

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * Validate a magic link token and return JWT access/refresh tokens.
 * @summary Login via magic link
 */
export const accountAuthMagicLinkLoginRetrieve = (
  token: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<MagicLinkLoginResponse>(
    {
      url: `/api/account/auth/magic-link/login/${token}/`,
      method: "GET",
      signal,
    },
    options
  );
};

export const getAccountAuthMagicLinkLoginRetrieveQueryKey = (token: string) => {
  return [`/api/account/auth/magic-link/login/${token}/`] as const;
};

export const getAccountAuthMagicLinkLoginRetrieveQueryOptions = <
  TData = Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>,
  TError = MagicLinkErrorResponse
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getAccountAuthMagicLinkLoginRetrieveQueryKey(token);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>
  > = ({ signal }) =>
    accountAuthMagicLinkLoginRetrieve(token, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!token,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AccountAuthMagicLinkLoginRetrieveQueryResult = NonNullable<
  Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>
>;
export type AccountAuthMagicLinkLoginRetrieveQueryError =
  MagicLinkErrorResponse;

export function useAccountAuthMagicLinkLoginRetrieve<
  TData = Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>,
  TError = MagicLinkErrorResponse
>(
  token: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>,
          TError,
          Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAccountAuthMagicLinkLoginRetrieve<
  TData = Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>,
  TError = MagicLinkErrorResponse
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>,
          TError,
          Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAccountAuthMagicLinkLoginRetrieve<
  TData = Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>,
  TError = MagicLinkErrorResponse
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Login via magic link
 */

export function useAccountAuthMagicLinkLoginRetrieve<
  TData = Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>,
  TError = MagicLinkErrorResponse
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountAuthMagicLinkLoginRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getAccountAuthMagicLinkLoginRetrieveQueryOptions(
    token,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Send a magic link to the provided email address. If the email is new, a user account will be created. The magic link expires in 15 minutes.
 * @summary Request magic link
 */
export const accountAuthMagicLinkRequestCreate = (
  requestMagicLink: RequestMagicLink,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<MagicLinkRequestResponse>(
    {
      url: `/api/account/auth/magic-link/request/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: requestMagicLink,
      signal,
    },
    options
  );
};

export const getAccountAuthMagicLinkRequestCreateMutationOptions = <
  TError = MagicLinkErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountAuthMagicLinkRequestCreate>>,
    TError,
    { data: RequestMagicLink },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountAuthMagicLinkRequestCreate>>,
  TError,
  { data: RequestMagicLink },
  TContext
> => {
  const mutationKey = ["accountAuthMagicLinkRequestCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountAuthMagicLinkRequestCreate>>,
    { data: RequestMagicLink }
  > = (props) => {
    const { data } = props ?? {};

    return accountAuthMagicLinkRequestCreate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountAuthMagicLinkRequestCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountAuthMagicLinkRequestCreate>>
>;
export type AccountAuthMagicLinkRequestCreateMutationBody = RequestMagicLink;
export type AccountAuthMagicLinkRequestCreateMutationError =
  MagicLinkErrorResponse;

/**
 * @summary Request magic link
 */
export const useAccountAuthMagicLinkRequestCreate = <
  TError = MagicLinkErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountAuthMagicLinkRequestCreate>>,
    TError,
    { data: RequestMagicLink },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountAuthMagicLinkRequestCreate>>,
  TError,
  { data: RequestMagicLink },
  TContext
> => {
  const mutationOptions =
    getAccountAuthMagicLinkRequestCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Soft delete user account. Prefixes unique fields to allow reuse of username/email.
 */
export const accountDeleteCreate = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<AccountDeletionResponse>(
    { url: `/api/account/delete/`, method: "POST", signal },
    options
  );
};

export const getAccountDeleteCreateMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountDeleteCreate>>,
    TError,
    void,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountDeleteCreate>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["accountDeleteCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountDeleteCreate>>,
    void
  > = () => {
    return accountDeleteCreate(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountDeleteCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountDeleteCreate>>
>;

export type AccountDeleteCreateMutationError = unknown;

export const useAccountDeleteCreate = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountDeleteCreate>>,
    TError,
    void,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountDeleteCreate>>,
  TError,
  void,
  TContext
> => {
  const mutationOptions = getAccountDeleteCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Log in a user and receive access and refresh tokens
 */
export const accountLoginCreate = (
  loginRequest: LoginRequest,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<AuthToken>(
    {
      url: `/api/account/login/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: loginRequest,
      signal,
    },
    options
  );
};

export const getAccountLoginCreateMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountLoginCreate>>,
    TError,
    { data: LoginRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountLoginCreate>>,
  TError,
  { data: LoginRequest },
  TContext
> => {
  const mutationKey = ["accountLoginCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountLoginCreate>>,
    { data: LoginRequest }
  > = (props) => {
    const { data } = props ?? {};

    return accountLoginCreate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountLoginCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountLoginCreate>>
>;
export type AccountLoginCreateMutationBody = LoginRequest;
export type AccountLoginCreateMutationError = unknown;

export const useAccountLoginCreate = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountLoginCreate>>,
    TError,
    { data: LoginRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountLoginCreate>>,
  TError,
  { data: LoginRequest },
  TContext
> => {
  const mutationOptions = getAccountLoginCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Request a password reset email. A reset link will be sent to the provided email address if it exists in our system.
 */
export const accountPasswordResetCreate = (
  passwordResetRequest: PasswordResetRequest,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<PasswordResetResponse>(
    {
      url: `/api/account/password/reset/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: passwordResetRequest,
      signal,
    },
    options
  );
};

export const getAccountPasswordResetCreateMutationOptions = <
  TError = AccountPasswordResetCreate400,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountPasswordResetCreate>>,
    TError,
    { data: PasswordResetRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountPasswordResetCreate>>,
  TError,
  { data: PasswordResetRequest },
  TContext
> => {
  const mutationKey = ["accountPasswordResetCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountPasswordResetCreate>>,
    { data: PasswordResetRequest }
  > = (props) => {
    const { data } = props ?? {};

    return accountPasswordResetCreate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountPasswordResetCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountPasswordResetCreate>>
>;
export type AccountPasswordResetCreateMutationBody = PasswordResetRequest;
export type AccountPasswordResetCreateMutationError =
  AccountPasswordResetCreate400;

export const useAccountPasswordResetCreate = <
  TError = AccountPasswordResetCreate400,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountPasswordResetCreate>>,
    TError,
    { data: PasswordResetRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountPasswordResetCreate>>,
  TError,
  { data: PasswordResetRequest },
  TContext
> => {
  const mutationOptions = getAccountPasswordResetCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Reset user's password using the token from the reset email
 */
export const accountPasswordResetConfirmCreate = (
  passwordResetConfirm: PasswordResetConfirm,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<PasswordResetResponse>(
    {
      url: `/api/account/password/reset/confirm/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: passwordResetConfirm,
      signal,
    },
    options
  );
};

export const getAccountPasswordResetConfirmCreateMutationOptions = <
  TError = AccountPasswordResetConfirmCreate400,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountPasswordResetConfirmCreate>>,
    TError,
    { data: PasswordResetConfirm },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountPasswordResetConfirmCreate>>,
  TError,
  { data: PasswordResetConfirm },
  TContext
> => {
  const mutationKey = ["accountPasswordResetConfirmCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountPasswordResetConfirmCreate>>,
    { data: PasswordResetConfirm }
  > = (props) => {
    const { data } = props ?? {};

    return accountPasswordResetConfirmCreate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountPasswordResetConfirmCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountPasswordResetConfirmCreate>>
>;
export type AccountPasswordResetConfirmCreateMutationBody =
  PasswordResetConfirm;
export type AccountPasswordResetConfirmCreateMutationError =
  AccountPasswordResetConfirmCreate400;

export const useAccountPasswordResetConfirmCreate = <
  TError = AccountPasswordResetConfirmCreate400,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountPasswordResetConfirmCreate>>,
    TError,
    { data: PasswordResetConfirm },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountPasswordResetConfirmCreate>>,
  TError,
  { data: PasswordResetConfirm },
  TContext
> => {
  const mutationOptions =
    getAccountPasswordResetConfirmCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Register a new user account
 */
export const accountRegisterCreate = (
  registration: Registration,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<AccountRegisterCreate201>(
    {
      url: `/api/account/register/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: registration,
      signal,
    },
    options
  );
};

export const getAccountRegisterCreateMutationOptions = <
  TError = AccountRegisterCreate400,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountRegisterCreate>>,
    TError,
    { data: Registration },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountRegisterCreate>>,
  TError,
  { data: Registration },
  TContext
> => {
  const mutationKey = ["accountRegisterCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountRegisterCreate>>,
    { data: Registration }
  > = (props) => {
    const { data } = props ?? {};

    return accountRegisterCreate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountRegisterCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountRegisterCreate>>
>;
export type AccountRegisterCreateMutationBody = Registration;
export type AccountRegisterCreateMutationError = AccountRegisterCreate400;

export const useAccountRegisterCreate = <
  TError = AccountRegisterCreate400,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountRegisterCreate>>,
    TError,
    { data: Registration },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountRegisterCreate>>,
  TError,
  { data: Registration },
  TContext
> => {
  const mutationOptions = getAccountRegisterCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Refresh an expired access token using a valid refresh token
 */
export const accountTokenRefreshCreate = (
  tokenRefreshRequest: TokenRefreshRequest,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<TokenRefreshResponse>(
    {
      url: `/api/account/token/refresh/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: tokenRefreshRequest,
      signal,
    },
    options
  );
};

export const getAccountTokenRefreshCreateMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountTokenRefreshCreate>>,
    TError,
    { data: TokenRefreshRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountTokenRefreshCreate>>,
  TError,
  { data: TokenRefreshRequest },
  TContext
> => {
  const mutationKey = ["accountTokenRefreshCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountTokenRefreshCreate>>,
    { data: TokenRefreshRequest }
  > = (props) => {
    const { data } = props ?? {};

    return accountTokenRefreshCreate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountTokenRefreshCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountTokenRefreshCreate>>
>;
export type AccountTokenRefreshCreateMutationBody = TokenRefreshRequest;
export type AccountTokenRefreshCreateMutationError = unknown;

export const useAccountTokenRefreshCreate = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountTokenRefreshCreate>>,
    TError,
    { data: TokenRefreshRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountTokenRefreshCreate>>,
  TError,
  { data: TokenRefreshRequest },
  TContext
> => {
  const mutationOptions = getAccountTokenRefreshCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Verify user email address
 */
export const accountVerifyEmailRetrieve = (
  uidb64: string,
  token: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<EmailVerificationResponse>(
    {
      url: `/api/account/verify-email/${uidb64}/${token}/`,
      method: "GET",
      signal,
    },
    options
  );
};

export const getAccountVerifyEmailRetrieveQueryKey = (
  uidb64: string,
  token: string
) => {
  return [`/api/account/verify-email/${uidb64}/${token}/`] as const;
};

export const getAccountVerifyEmailRetrieveQueryOptions = <
  TData = Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>,
  TError = EmailVerificationError
>(
  uidb64: string,
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getAccountVerifyEmailRetrieveQueryKey(uidb64, token);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>
  > = ({ signal }) =>
    accountVerifyEmailRetrieve(uidb64, token, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(uidb64 && token),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AccountVerifyEmailRetrieveQueryResult = NonNullable<
  Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>
>;
export type AccountVerifyEmailRetrieveQueryError = EmailVerificationError;

export function useAccountVerifyEmailRetrieve<
  TData = Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>,
  TError = EmailVerificationError
>(
  uidb64: string,
  token: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>,
          TError,
          Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAccountVerifyEmailRetrieve<
  TData = Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>,
  TError = EmailVerificationError
>(
  uidb64: string,
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>,
          TError,
          Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAccountVerifyEmailRetrieve<
  TData = Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>,
  TError = EmailVerificationError
>(
  uidb64: string,
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useAccountVerifyEmailRetrieve<
  TData = Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>,
  TError = EmailVerificationError
>(
  uidb64: string,
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountVerifyEmailRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getAccountVerifyEmailRetrieveQueryOptions(
    uidb64,
    token,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
